import { addDays } from 'date-fns'
import { CATEGORIES, DELIVERY_SUBTYPES, DELIVERY_WINDOWS, MATTRESS_REMOVAL_SKUS, deliveryTypes } from '@/config/globals'

/**
 * @typedef {Object} Cart
 * @property {Object[]} items
 */

/**
 * Check if cart has white glove delivery
 * @param {Array} cartItems
 * @returns {Boolean}
 */
export const hasWhiteGlove = (cartItems = []) => {
    return cartItems && cartItems.some((item) => item.deliveryType === deliveryTypes.whiteGlove)
}

/**
 * Check if cart has fedex delivery
 * @param {Array} cartItems
 * @returns {Boolean}
 */
export const hasFedex = (cartItems = []) => {
    return cartItems && cartItems.some((item) => item.deliveryType === deliveryTypes.direct)
}

/**
 * Check if the cart has mattress removal skus
 * @param {Cart | undefined} cart
 * @returns {boolean}
 */
export const hasMattressRemovalItem = (cart) => {
    if (cart && cart.items) {
        const skusInCart = cart.items.some(({ sku }) => MATTRESS_REMOVAL_SKUS.includes(sku))
        return skusInCart
    }
}

/**
 * Get formated date
 * @param {number} daysToAdd
 * @returns {string}
 */
export const getFormattedDate = (daysToAdd = 0, startDate = new Date()) => {
    const newDate = addDays(startDate, daysToAdd)
    const shortMonth = newDate.toLocaleDateString('en-us', { month: 'long' })
    const day = newDate.getDate()
    return `${shortMonth} ${day}`
}

/**
 * Format the delivery window
 * @param {number} start Start window in days
 * @param {number} end End window in days
 * @returns {{startWindow: string, endWindow: string}}
 */
export const formatDeliveryWindow = (start = 0, end = 0) => {
    const startWindow = getFormattedDate(start)
    const endWindow = getFormattedDate(end)
    return { startWindow, endWindow }
}

/**
 * Get shipment delivery estimate by delivery type
 * @param {{ title: string, type: string, estimatedDelivery: string, items: Array }} shipment
 * @returns {string}
 */
export const getShipmentDeliveryEstimate = (shipment = {}) => {
    const deliverySubType = getDeliverySubType(shipment)
    let formatedDeliveryWindow = {}

    // White glove delivery estimates
    switch (deliverySubType) {
        case DELIVERY_SUBTYPES.MATTRESS_AND_FURNITURE:
        case DELIVERY_SUBTYPES.FURNITURE_ONLY:
            formatedDeliveryWindow = formatDeliveryWindow(
                DELIVERY_WINDOWS.whiteGloveFurniture.start,
                DELIVERY_WINDOWS.whiteGloveFurniture.end
            )
            break
        case DELIVERY_SUBTYPES.MATTRESS_ONLY:
            formatedDeliveryWindow = formatDeliveryWindow(
                DELIVERY_WINDOWS.whiteGloveMattress.start,
                DELIVERY_WINDOWS.whiteGloveMattress.end
            )
            break
        case DELIVERY_SUBTYPES.FEDEX:
            formatedDeliveryWindow = formatDeliveryWindow(DELIVERY_WINDOWS.fedex.start, DELIVERY_WINDOWS.fedex.end)
            break
        default:
            break
    }

    return `Estimated delivery: ${formatedDeliveryWindow.startWindow} - ${formatedDeliveryWindow.endWindow}`
}

/**
 * Get delivery sub type base on the items
 * @param {{ title: string, type: string, estimatedDelivery: string, items: Array }} shipment
 * @returns {'mattressAndFurniture' | 'mattressOnly' | 'furnitureOnly' | 'fedex'}
 */
export const getDeliverySubType = (shipment = {}) => {
    let deliverySubType = ''

    const whiteGloveCategories = new Set()
    
    if (shipment.type === 'whiteGlove') {
        if (shipment.items.some(item => item.category.toLowerCase() === CATEGORIES.MATTRESSES)) whiteGloveCategories.add(CATEGORIES.MATTRESSES)
        if (shipment.items.some(item => item.category.toLowerCase() === CATEGORIES.FURNITURE)) whiteGloveCategories.add(CATEGORIES.FURNITURE)
        
        switch (true) {
            case whiteGloveCategories.has(CATEGORIES.MATTRESSES) && whiteGloveCategories.has(CATEGORIES.FURNITURE):
                deliverySubType = DELIVERY_SUBTYPES.MATTRESS_AND_FURNITURE
                break
            case whiteGloveCategories.has(CATEGORIES.MATTRESSES):
                deliverySubType = DELIVERY_SUBTYPES.MATTRESS_ONLY
                break
            case whiteGloveCategories.has(CATEGORIES.FURNITURE):
                deliverySubType = DELIVERY_SUBTYPES.FURNITURE_ONLY
                break
            default:
                deliverySubType = ''
                break
        }
    } else {
        deliverySubType = DELIVERY_SUBTYPES.FEDEX
    }

    return deliverySubType
}
